import { ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import TableLink from "./TableLink";
import Icon, { IconColor } from "../Icon";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";

import styles from "./Table.module.css";

export interface TableCellProps {
  value: any;
  leftIcon?: string;
  rightIcon?: string;
  leftIconTooltip?: string;
  rightIconTooltip?: string;
  linkTo?: string;
  className?: string;
  iconColor?: IconColor;
  iconSize?: number;
  fromDashboard?: boolean;
  onClick?: () => void;
  cellStyles?: React.CSSProperties;
}

export const TableCell = ({
  value,
  leftIcon = undefined,
  rightIcon = undefined,
  leftIconTooltip = undefined,
  rightIconTooltip = undefined,
  linkTo = undefined,
  className = undefined,
  iconColor = undefined,
  iconSize = undefined,
  fromDashboard,
  onClick,
  cellStyles,
}: TableCellProps) => {
  let leftIconContent: ReactNode | null = null;
  let rightIconContent: ReactNode | null = null;
  const rightIconId = `right-${uuidv4()}`;
  const leftIconId = `left-${uuidv4()}`;
  if (leftIcon) {
    const leftIconContentTemp = (
      <Icon
        name={leftIcon}
        size={iconSize ?? 24}
        color={iconColor ?? "Primary-700"}
        className="mr-6"
      />
    );
    if (leftIconTooltip) {
      leftIconContent = (
        <>
          <div id={leftIconId} className="line-height-0">
            {leftIconContentTemp}
          </div>
          <PrimeTooltip target={`#${leftIconId}`} content={leftIconTooltip} />
        </>
      );
    } else {
      leftIconContent = leftIconContentTemp;
    }
  }
  if (rightIcon) {
    const rightIconContentTemp = (
      <Icon
        name={rightIcon}
        size={24}
        color={iconColor ?? "Primary-700"}
        className="ml-6"
      />
    );
    if (rightIconTooltip) {
      rightIconContent = (
        <>
          <span id={rightIconId} className="line-height-0">
            {rightIconContentTemp}
          </span>
          <PrimeTooltip target={`#${rightIconId}`} content={rightIconContent} />
        </>
      );
    } else {
      rightIconContent = rightIconContentTemp;
    }
  }

  const cellContent = onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={classNames(styles.tableLink, styles.linkHover)}
    >
      {leftIconContent}
      <span>{value}</span>
      {rightIconContent}
    </button>
  ) : (
    <>
      {leftIconContent}
      <span>{value}</span>
      {rightIconContent}
    </>
  );

  return (
    <div style={cellStyles} className={classNames(styles.tableCell, className)}>
      {linkTo && value && (
        <TableLink to={linkTo} fromDashboard={fromDashboard}>
          {cellContent}
        </TableLink>
      )}
      {!linkTo && value && cellContent}
    </div>
  );
};

export interface MissingTableCellProps extends TableCellProps {
  missingValueText: string;
}

export const MissingValueTableCell = ({
  value,
  leftIcon = undefined,
  rightIcon = undefined,
  leftIconTooltip = undefined,
  rightIconTooltip = undefined,
  linkTo = undefined,
  className = undefined,
  iconColor = undefined,
  iconSize = undefined,
  missingValueText,
  fromDashboard,
  onClick,
  cellStyles,
}: MissingTableCellProps) => {
  const val = value || missingValueText;
  let combinedClassName = className;

  return (
    <TableCell
      value={val}
      leftIcon={leftIcon}
      leftIconTooltip={leftIconTooltip}
      rightIcon={rightIcon}
      rightIconTooltip={rightIconTooltip}
      linkTo={linkTo}
      iconColor={iconColor}
      className={combinedClassName}
      iconSize={iconSize}
      cellStyles={cellStyles}
      fromDashboard={fromDashboard}
      onClick={onClick}
    />
  );
};
export default observer(TableCell);
